import type { ThemeMode } from '../theme';

// Animation duration tokens — shared across all themes. Used by the InlineEdit
// primitive set (5.5a) for hover affordances, edit-mode transitions, and the
// success-pulse glow. Three tiers cover the common cases:
//   --transition-fast  (120ms): hover state changes, cursor swaps
//   --transition-base  (180ms): edit-mode enter/exit, save pulse
//   --transition-slow  (240ms): larger layout shifts (rare)
// Theme-agnostic on purpose — keep them outside the per-mode maps.
//
// Part 6.1 B1: easing tokens. --ease-out for the standard "decelerate
// to rest" curve; --ease-overshoot for entrances that feel snappy
// (slight scale past 1.0 then settle). Both are CSS cubic-bezier
// literals so they can be dropped into any `transition` or `animation`
// shorthand alongside the duration tokens.
//
// Design System 2.0 (D-011, tokens.json `duration`): the system's own motion
// names join them. --dur-state is the 120ms every hover, focus and press uses;
// --dur-lift (250ms) a card lifting, a modal rising, a drawer sliding;
// --dur-rise (600ms) a block arriving, --stagger (60ms) apart, capped at ten
// steps; --dur-grow a bar filling; --dur-count a number counting up. The
// --transition-* names stay for the InlineEdit set until it moves.
const TRANSITION_TOKENS: Record<string, string> = {
  '--transition-fast': '120ms',
  '--transition-base': '180ms',
  '--transition-slow': '240ms',
  '--ease-out': 'cubic-bezier(0.16, 1, 0.3, 1)',
  '--ease-overshoot': 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  '--dur-state': '120ms',
  '--dur-lift': '250ms',
  '--dur-rise': '600ms',
  '--stagger': '60ms',
  '--dur-grow': '1100ms',
  '--dur-count': '1200ms',
  '--tooltip-delay': '400ms',
  '--skeleton-delay': '300ms',
  '--toast-undo': '6s',
  '--toast-plain': '2.6s',
};

// Design System 2.0 (tokens.json `shadow`, `radius`, `spacing`, and the parts
// stylesheet's type sizes): the non-colour tokens, the same in every mode.
//   Shadows (D-010) — none at rest; --lift on hover only (with translateY(-3px)),
//   --lift-indigo for the one call to action, --focus-field around a focused
//   field, --pop for menus / popovers / pickers, --modal for modals and drawers,
//   --toast for toasts. The three that carry colour read the mode-aware
//   triplets, so one declaration serves day, night and sepia.
//   Corners (D-009, D-018) — 3 tags · 4 controls · 6 panels · round.
//   Spacing (D-014) and the type scale (D-005): the published --fs-* names at
//   the stylesheet's root values (the five tiered sizes at the 16-inch step;
//   HubPage steps them with the screen).
export const SYSTEM_TOKENS: Record<string, string> = {
  '--lift': '0 16px 40px rgba(var(--overlay-rgb), 0.10)',
  '--lift-indigo': '0 12px 26px rgba(var(--accent-primary-rgb), 0.30)',
  '--focus-field': '0 0 0 3px var(--indigo-soft)',
  '--pop': '0 22px 48px -14px rgba(22, 24, 27, 0.42)', // D-053: a touch deeper, so a menu stands off the page
  '--modal': '0 28px 70px -20px rgba(0, 0, 0, 0.45)',
  '--toast': '0 14px 38px -10px rgba(0, 0, 0, 0.42)',
  '--r-tag': '3px',
  '--r-control': '4px',
  '--r-panel': '6px',
  '--r-round': '999px',
  '--space-2': '2px', '--space-4': '4px', '--space-6': '6px', '--space-8': '8px',
  '--space-10': '10px', '--space-12': '12px', '--space-14': '14px', '--space-16': '16px',
  '--space-20': '20px', '--space-24': '24px', '--space-28': '28px', '--space-32': '32px',
  '--space-40': '40px', '--space-48': '48px', '--space-64': '64px',
  '--fs-display': '104px', '--fs-big': '64px', '--fs-hero': '40px', '--fs-section': '30px',
  '--fs-stat': '34px', '--fs-figure': '28px', '--fs-tile': '22px', '--fs-title': '20px', '--fs-panel': '16px', // D-054: the panel title step
  '--fs-body': '14px', '--fs-sub': '13.5px', '--fs-small': '12.5px', '--fs-kicker': '11px',
  '--fs-micro': '10.5px',
};

// Wave M (mobile sales app, 2026-07): mode-INVARIANT Carbon band tokens.
// Carbon bands (pipeline-pulse card, in-call sheet, toasts, contract header)
// stay dark in day, sepia, AND night — they mirror designSystem.tsx BRAND
// (the D-05 carve-out) as CSS vars so mobile surfaces can read them without
// importing the constant. --ink is NOT a substitute: it flips light at night.
//
// MOBILE-2E P1 (mobile second edition): the rest of the approved design's
// mode-invariant set (refactor/design-sync/mobile-second-edition/canvas/
// m2-tokens.css), values copied exactly. --on-carbon-* are the softer inks
// and the hairline on a carbon band; --scrim is the veil behind a sheet. The scrim is
// a dark veil in every mode on purpose — the old rgba(var(--overlay-rgb))
// scrim turned WHITE in night mode.
//
// Design System 2.0 (D-004): ONE carbon family — #16181B / #202327 / #2A2E33,
// ink #F4F4F5 at 1 / .78 / .60 — replacing the five carbons (#191919,
// #242424 and #212327 retire). D-043: the hairline on carbon is named with the
// rules (--rule-on-carbon); --on-carbon-rule stays as an alias until the last
// file moves. --on-indigo left this set: it is carbon in night (D-043), so it
// lives in the per-mode maps below with --on-paprika and --on-status-on-track.
export const CARBON_TOKENS: Record<string, string> = {
  '--carbon': '#16181B',
  '--carbon-2': '#202327',  // a raised surface on carbon (hero tiles)
  '--on-carbon': '#F4F4F5',
  '--on-carbon-soft': 'rgba(244, 244, 245, 0.78)',
  '--on-carbon-faint': 'rgba(244, 244, 245, 0.60)',
  '--rule-on-carbon': 'rgba(244, 244, 245, 0.16)',
  '--on-carbon-rule': 'var(--rule-on-carbon)',
  '--scrim': 'rgba(10, 11, 13, 0.44)',
  // MOBILE-2E P3 review round 1: the incoming call's Answer disc, on the carbon
  // ring screen, under a white glyph. The boards paint it --status-on-track,
  // whose NIGHT value (#6FCB74) gives the white handset ~2:1; this is that
  // token's day value, held in every mode (5.1:1 under the white --on-brand).
  '--carbon-answer': '#2E7D32',
  // SA-1a (Systems Assessment, 2026-10-26): the approved Client Metrics and
  // Systems Assessment canvases' accents on a carbon band — a raised tile, and
  // indigo, paprika and positive brightened to read on carbon in every mode
  // (the day --indigo is ~2.6:1 on carbon; these are 4.5:1 or better).
  '--carbon-3': '#2A2E33',  // the highest lift on carbon (D-004)
  '--indigo-on-carbon': '#7C9CFF',
  '--paprika-on-carbon': '#FF8A4C',
  '--positive-on-carbon': '#7BD88F',
};

// Mission Control 2.0 (Entry 536): Art Kit 1.0's nominal badge metals
// (badge-tokens.json), the same in every mode, applied beside the carbon set.
// They guide the painted badge's shades and fill an agent's level bar; never a
// creature's body, never a status colour.
export const BADGE_TOKENS: Record<string, string> = {
  '--badge-none': '#7D9AB8',
  '--badge-bronze': '#B78355',
  '--badge-silver': '#B9C8D7',
  '--badge-gold': '#E8BF68',
  '--badge-platinum': '#DDD9F0',
  '--badge-diamond': '#96D6FF',
};

// CD-12 / 4A: the HubRail palette as tokens. The rail is the ONE constant dark
// surface across every app mode — Light & Dark keep neutral Carbon; only Sepia
// swaps it to the warm eye-comfort variant. Spread into the per-mode maps below
// (neutral → dark + light, warm → sepia) so HubRail reads var(--rail-*) and the
// mode switch is automatic. Active accent is the on-carbon lift of --indigo
// (#5A82FF neutral / amber in sepia); paprika is the single at-risk signal.
// D-004: the rail sits on the one carbon; its lift moves #212327 → #202327.
const RAIL_TOKENS_NEUTRAL: Record<string, string> = {
  '--rail-ground': '#16181B',
  '--rail-lift': '#202327',
  '--rail-hair': 'rgba(255,255,255,0.085)',
  '--rail-hair-soft': 'rgba(255,255,255,0.05)',
  '--rail-text': 'rgba(242,242,242,0.72)',
  '--rail-hi': '#F2F2F2',
  '--rail-faint': 'rgba(242,242,242,0.42)',
  '--rail-ghost': 'rgba(242,242,242,0.30)',
  '--rail-icon': 'rgba(242,242,242,0.56)',
  '--rail-hover': 'rgba(255,255,255,0.055)',
  '--rail-active': '#5A82FF',
  '--rail-active-tint': 'rgba(90,130,255,0.14)',
  '--rail-paprika': '#F76100',
};
const RAIL_TOKENS_SEPIA: Record<string, string> = {
  '--rail-ground': '#241D12',
  '--rail-lift': '#31281A',
  '--rail-hair': 'rgba(244,234,214,0.10)',
  '--rail-hair-soft': 'rgba(244,234,214,0.055)',
  '--rail-text': 'rgba(245,238,224,0.74)',
  '--rail-hi': '#F6F0E3',
  '--rail-faint': 'rgba(245,238,224,0.44)',
  '--rail-ghost': 'rgba(245,238,224,0.30)',
  '--rail-icon': 'rgba(245,238,224,0.58)',
  '--rail-hover': 'rgba(255,244,222,0.06)',
  '--rail-active': '#E3A64E',
  '--rail-active-tint': 'rgba(227,166,78,0.15)',
  '--rail-paprika': '#F76100',
};

// Portal V2 (K1A, 2026-09-14): V2's night map, verbatim from the design
// package's index.html `[data-mode="night"]` block
// (refactor/design-sync/portal-free-app-v2/…/index.html:18-29). The portal
// applies it as inline custom properties on its own root (`V2Root
// mode="night"`); the staff app's dark map above is untouched — V2's night
// values differ from it (V2's --paper-2 is LIGHTER than its paper, the app's
// is darker), so neither can stand in for the other.
// Design System 2.0 (D-043) adds the renamed --ink-disabled (the old ghost
// value, aliased) and the three on- inks a night fill takes: carbon, because
// white on V2's bright night indigo, paprika and green falls under 4.5:1.
export const V2_NIGHT_TOKENS: Record<string, string> = {
  '--ink': '#EDEEF0', '--ink-soft': '#C3C7CD', '--ink-faint': '#8E959E', '--ink-disabled': '#5F656D', '--ink-ghost': 'var(--ink-disabled)',
  '--on-indigo': '#16181B', '--on-paprika': '#16181B', '--on-status-on-track': '#16181B',
  '--paper': '#1D2126', '--paper-2': '#262B31', '--bg': '#141619',
  '--rule': 'rgba(237,238,240,.14)', '--rule-soft': 'rgba(237,238,240,.07)', '--rule-strong': 'rgba(237,238,240,.3)',
  '--indigo': '#537BFF', '--indigo-ink': '#8CA5FF', '--indigo-soft': 'rgba(83,123,255,.17)', '--indigo-on-soft': '#8CA5FF',
  '--paprika': '#FF7A26', '--paprika-ink': '#FFA25E', '--paprika-soft': 'rgba(255,122,38,.16)', '--paprika-on-soft': '#FFA25E',
  '--status-on-track': '#66BB6A', '--status-on-track-soft': 'rgba(102,187,106,.16)', '--status-on-track-on-soft': '#A5D6A7',
  '--status-off-track': '#EF5350', '--status-off-track-soft': 'rgba(239,83,80,.15)', '--status-off-track-on-soft': '#EF9A9A',
  '--status-warning': '#FFA726', '--status-warning-soft': 'rgba(255,167,38,.16)', '--status-warning-on-soft': '#FFCC80',
  '--status-neutral': '#8E959E', '--status-neutral-soft': 'rgba(142,149,158,.16)', '--status-neutral-on-soft': '#C3C7CD',
  '--donut-track': '#31363D', '--progress-track': '#31363D',
  '--rm-edge': 'rgba(237,238,240,.34)', '--rm-edge-live': 'rgba(237,238,240,.6)', '--rm-dot': 'rgba(237,238,240,.055)',
};

// Portal V2 (K1A): the design's `:root` dot-grid value (index.html:17). Night's
// is in V2_NIGHT_TOKENS; both are spread into the per-mode maps below.
const V2_RM_DOT_DAY = 'rgba(22,24,27,.07)';

// Portal V2 (K1A): mode-INVARIANT tokens for the literals the design paints on
// its fixed carbon surfaces (the rail, the bulk bar, the staff band, the toast,
// the camera cluster), plus the skeleton sweep and the design's easing names.
// Deliberately NOT aliased to --rail-*: those swap to the warm set in sepia
// (RAIL_TOKENS_SEPIA above), and these read the same in every mode. --e-out /
// --e-over are the SAME curves as --ease-out / --ease-overshoot, so they take
// those exact strings rather than restating them. --v2-skel-sweep holds var()
// references, which resolve where the property is declared — V2Root re-declares
// the whole set on its own root so the sweep follows V2's night tokens there.
export const V2_TOKENS: Record<string, string> = {
  '--v2-carbon': '#16181B',
  '--v2-carbon-lift': '#202327', // D-004: #212327 retires
  '--v2-on-carbon': '#F2F2F2',
  '--v2-on-carbon-soft': 'rgba(242,242,242,.72)',
  '--v2-on-carbon-faint': 'rgba(242,242,242,.42)',
  '--v2-on-carbon-ghost': 'rgba(242,242,242,.3)',
  '--v2-on-carbon-icon': 'rgba(242,242,242,.56)',
  '--v2-carbon-hair': 'rgba(255,255,255,.085)',
  '--v2-carbon-hover': 'rgba(255,255,255,.055)',
  '--v2-carbon-indigo': '#5A82FF',
  '--v2-carbon-indigo-tint': 'rgba(90,130,255,.14)',
  '--v2-carbon-paprika': '#FFB08A',
  '--v2-carbon-paprika-line': 'rgba(255,176,138,.32)',
  '--v2-skel-sweep': 'linear-gradient(90deg,var(--paper-2) 0%,var(--rule-soft) 40%,var(--paper-2) 80%)',
  '--e-out': TRANSITION_TOKENS['--ease-out'],
  '--e-over': TRANSITION_TOKENS['--ease-overshoot'],
};

// 5.5c A4: brand surface + text tokens are theme-aware via cssVariables.
// Previously the UnifiedCardDetailModal declared these on its .root with
// light-mode literals + JS-driven re-application of MUI palette; the JS
// path missed every popover that portaled to document.body (DateTimePicker,
// InlineEdit popovers, TodoComposerDialog, the add-flow dialogs), so dark
// mode showed light-mode contrast inside the popovers. Moving the tokens
// to :root via this module's applyThemeCssVariables() fixes every consumer
// at once — modal AND portals AND any future surface.
//
// Tokens added in this set:
//   --ink         primary text  (formerly hardcoded #191919)
//   --ink-soft    secondary text
//   --ink-faint   tertiary text / labels / placeholders (D-043)
//   --ink-disabled disabled things and decorative marks (was --ink-ghost)
//   --paper       primary surface
//   --paper-2     subtle alt surface (modal body, drop-zones, etc.)
//   --bg          page background
//   --rule        default border
//   --rule-soft   subtler border
//   --rule-strong emphasis border
//   --indigo, --indigo-ink, --paprika, --paprika-ink (brand accents that
//     don't strictly need to theme-shift but living here keeps consumers
//     from re-declaring them)
//
// Light values match the existing UnifiedCardDetailModal :root defaults
// for visual continuity. Dark + sepia values are tuned for WCAG AA
// contrast against the per-theme paper surface.

// DES-W1 (D-12, 2026-06-12): night Revised set per the approved contrast
// pitch — contrastPitch/contrastPalettes.ts `nightRevised`, receipts in
// refactor/app-atlas/08-contrast-proposal.md §3/§4. Deeper page, lifted
// paper, visible hairlines; the #5C5C5C lifted-gray header relic joins
// the token ladder (DS-003). --sticky-bg-alt (#454545, same relic) joins
// at the paper-2 step — the pitch named only the header pair, but leaving
// it would render a mid-gray slab over the new ladder in sticky columns.
//
// Design System 2.0 (D-015): the chart ramp matches ChartComponents.tsx's
// CHART_COLORS.default — indigo leads (each mode's own --indigo), then slate,
// amber, light indigo, muted slate, light amber, the same in every mode.
// Paprika is never in the ramp; --chart-attention is the one series that
// must scream. (--chart-2 was paprika until 2.0.)
const CHART_RAMP: Record<string, string> = {
  '--chart-2': '#5B7C99',
  '--chart-3': '#F2A93B',
  '--chart-4': '#3D9CFF',
  '--chart-5': '#7D9AB8',
  '--chart-6': '#FFC267',
  '--chart-attention': 'var(--paprika)',
};
const cssVarsDark: Record<string, string> = {
  ...RAIL_TOKENS_NEUTRAL,
  ...V2_TOKENS,
  ...CHART_RAMP,
  '--rm-dot': V2_NIGHT_TOKENS['--rm-dot'],
  '--header-bg': '#191B1F',
  '--header-bg-hover': '#22252B',
  '--sticky-bg-alt': '#191B1F',
  '--accent-primary': '#0443ef',
  '--accent-primary-rgb': '4, 67, 239',
  '--accent-secondary': '#f76100',
  '--accent-secondary-rgb': '247, 97, 0',
  '--accent-info': '#29b6f6',
  '--accent-error': '#f44336',
  '--donut-track': '#2c2c2c',
  '--text-muted': '#C6CAD1',
  '--chart-1': '#5B86FF',
  // The gap chart's owner ring and owner numbers (Phase 2d, InteractiveGapChart).
  '--slate': '#8FA9C4', '--slate-soft': 'rgba(143,169,196,.18)', '--slate-on-soft': '#B9CBDD',
  '--grid-line-rgb': '242, 242, 242',
  '--progress-track': '#2c2c2c',
  '--overlay-rgb': '255, 255, 255',
  '--header-text': '#F4F5F7',
  '--indigo-soft': 'rgba(91, 134, 255, 0.24)',
  '--paprika-soft': 'rgba(255, 125, 51, 0.22)',
  '--indigo-on-soft': '#B7C8FF',
  '--paprika-on-soft': '#FFBE91',
  '--paprika-nav-hover': 'rgba(247, 97, 0, 0.32)',
  // 5.5c brand tokens — dark theme.
  // Paper surfaces sit two stops above the page bg so cards/modals
  // separate visually from background even without borders.
  '--ink': '#F4F5F7',          // primary text — ~15.4:1 on --paper
  '--ink-soft': '#C6CAD1',     // secondary text — ~9.7:1
  '--ink-faint': '#939AA4',    // labels, placeholders — ~5.5:1 (AA with room)
  // D-043: disabled things and decorative marks only — sub-AA by design.
  // --ink-ghost is the old name, aliased until the last file moves.
  '--ink-disabled': '#6A707A',
  '--ink-ghost': 'var(--ink-disabled)',
  '--paper': '#1F2126',
  // DS2EOS IDS: the writer colours, one per person on a shared notebook, by
  // ((userId - 1) mod 8) + 1. Each reaches 4.5:1 on this mode's --paper, and
  // --on-indigo text reaches 4.5:1 on each.
  '--w-1': '#7C9CFF', '--w-2': '#F472B6', '--w-3': '#C084FC', '--w-4': '#2DD4BF',
  '--w-5': '#F59E0B', '--w-6': '#A8B3C2', '--w-7': '#38BDF8', '--w-8': '#4ADE80',
  '--paper-2': '#191B1F',
  '--bg': '#121316',
  '--rule': 'rgba(244, 245, 247, 0.15)',
  '--rule-soft': 'rgba(244, 245, 247, 0.07)',
  '--rule-strong': 'rgba(244, 245, 247, 0.30)',
  '--hover': 'rgba(244, 245, 247, 0.06)',   // D-053: lifts in Night (paper-2 is darker than paper here)
  '--indigo': '#5B86FF',       // brightened to AA on the darker paper
  '--indigo-ink': '#8FA9FF',
  // CD-25: the ONE mode-invariant ink — text/glyphs sitting ON a saturated
  // brand fill (a filled indigo button, a StatusTag, the paprika AI action).
  // White reads in day, sepia and night alike, which is why the design system
  // treats it as a deliberate constant; giving it a token name means component
  // CSS never has to reach for a raw literal to express it.
  '--on-brand': '#ffffff',
  // D-043: the text on a filled colour takes that colour's on- token. In night
  // all three fills are bright, so white falls under 4.5:1 (white on night
  // indigo is 3.3:1) and they take carbon. A night primary button therefore
  // has a dark label on the bright indigo.
  '--on-indigo': '#16181B',          // = --carbon
  '--on-paprika': '#16181B',         // = --carbon
  '--on-status-on-track': '#16181B', // = --carbon
  '--paprika': '#FF7D33',
  '--paprika-ink': '#FFA468',
  // Scorecard-rebuild P1 (AUD-112): the single semantic status-token set.
  // Every on/off-track chip, dot, and cell tint routes through these four
  // families — never raw MUI success/error and never paprika (paprika is
  // reserved for counts/attention). Triplet per family mirrors the
  // --indigo-soft / --indigo-on-soft convention: base (dot/line), -soft
  // (chip/cell background tint), -on-soft (text on that tint).
  '--status-on-track': '#6FCB74',
  '--status-on-track-soft': 'rgba(111, 203, 116, 0.22)',
  '--status-on-track-on-soft': '#A6E3A9',
  '--status-off-track': '#FF6259',
  '--status-off-track-soft': 'rgba(255, 98, 89, 0.22)',
  '--status-off-track-on-soft': '#FFA39B',
  '--status-warning': '#FFA726',
  '--status-warning-soft': 'rgba(255, 167, 38, 0.22)',
  '--status-warning-on-soft': '#FFC871',
  '--status-neutral': '#939AA4',
  '--status-neutral-soft': 'rgba(244, 245, 247, 0.12)',
  '--status-neutral-on-soft': '#C6CAD1',
  // Stage-playbook STEP KINDS (Project Process Builder). A step's kind is a
  // category, not a status, so it gets its own small ramp rather than
  // borrowing the status tokens. Email deliberately has no token — it uses
  // --indigo, because "the app writes to the client" is the brand action.
  '--k-sched': '#4FC3F7',
  '--k-sched-soft': 'rgba(79, 195, 247, 0.16)',
  '--k-flow': '#9D85FF',
  '--k-flow-soft': 'rgba(157, 133, 255, 0.18)',
  '--k-todo': '#8FA9C2',
  '--k-todo-soft': 'rgba(143, 169, 194, 0.16)',
  '--k-form': '#F0B25A',
  '--k-form-soft': 'rgba(240, 178, 90, 0.18)',
  '--k-new': '#5FD37A',
  '--k-new-soft': 'rgba(95, 211, 122, 0.18)',
  // Build blocks (the Build Block Scheduler). A build block is a scheduling
  // GEOMETRY, not a status and not a step kind — which is exactly why it gets
  // its own small family instead of borrowing --status-warning (that means
  // "off track") or --paprika (reserved for attention / the app acting on its
  // own). Same reasoning that gave --k-* its own ramp. Do not "simplify" it
  // onto status.
  // Family convention mirrors --indigo: base, -ink (the readable TEXT form),
  // -soft (the block/cell tint). The fourth, -line, is this family's HAIRLINE
  // and is deliberately an rgba of the base (never a hex) so it composites
  // over any paper — it is not a fifth ink.
  // Values are the approved specimen's [data-mode="night"] sweep.
  '--build': '#F0B34A',
  '--build-ink': '#F6CE8A',
  '--build-soft': 'rgba(240, 179, 74, 0.17)',
  '--build-line': 'rgba(240, 179, 74, 0.52)',
  // Tech Audit (TA-1, 2026-10-11): the BSG-only layer's warm tint — internal
  // notes, readiness, the team table's internal columns. The lock + "BSG only"
  // label is the signal; the tint backs it up. Values: the approved design.
  '--internal-soft': '#2A2520',
  '--internal-rule': '#4A3F2C',
  '--internal-ink': '#D9B26A',
};

// DES-W1 (D-12, 2026-06-12): day Revised set per the approved contrast
// pitch (`dayRevised`). A real page-vs-paper value step (white becomes
// the content surface, not the wash), visible hairlines (the v3 dial),
// and label ink to AA (--ink-faint 3.45:1 → 5.9:1 — CONTRAST-002).
/**
 * Exported so print/PDF builders can resolve brand colours WITHOUT hardcoding
 * hex. A printed client document must be light regardless of the app's active
 * theme, so it reads THIS map rather than the live custom properties (which
 * would come back dark in dark mode) — the same reason the public share page
 * forces light on mount.
 */
export const cssVarsLight: Record<string, string> = {
  ...RAIL_TOKENS_NEUTRAL,
  ...V2_TOKENS,
  ...CHART_RAMP,
  '--rm-dot': V2_RM_DOT_DAY,
  '--header-bg': '#FFFFFF',
  '--header-bg-hover': '#F1F2F4',
  '--sticky-bg-alt': '#fafafa',
  '--accent-primary': '#0443ef',
  '--accent-primary-rgb': '4, 67, 239',
  '--accent-secondary': '#f76100',
  '--accent-secondary-rgb': '247, 97, 0',
  '--accent-info': '#0288d1',
  '--accent-error': '#d32f2f',
  '--donut-track': '#e6e6e6',
  '--text-muted': 'rgba(22, 24, 27, 0.62)',
  '--chart-1': '#0443EF',
  // The gap chart's owner ring and owner numbers (Phase 2d, InteractiveGapChart).
  '--slate': '#5B7C99', '--slate-soft': 'rgba(91,124,153,.16)', '--slate-on-soft': '#3D5A73',
  '--grid-line-rgb': '25, 25, 25',
  '--progress-track': '#e6e6e6',
  '--overlay-rgb': '0, 0, 0',
  '--header-text': '#16181B',
  '--indigo-soft': 'rgba(4, 67, 239, 0.10)',
  '--paprika-soft': 'rgba(247, 97, 0, 0.12)',
  '--indigo-on-soft': '#0337C2',
  '--paprika-on-soft': '#B54A08',
  '--paprika-nav-hover': 'rgba(247, 97, 0, 0.12)',
  // 5.5c brand tokens — light theme.
  '--ink': '#16181B',
  '--ink-soft': '#3F444B',
  '--ink-faint': '#5F6670',
  '--ink-disabled': '#9AA0A8', // D-043 — see the dark map
  '--ink-ghost': 'var(--ink-disabled)',
  '--paper': '#FFFFFF',
  // DS2EOS IDS: the writer colours, one per person on a shared notebook, by
  // ((userId - 1) mod 8) + 1. Each reaches 4.5:1 on this mode's --paper, and
  // --on-indigo text reaches 4.5:1 on each.
  '--w-1': '#0443ef', '--w-2': '#be185d', '--w-3': '#7e22ce', '--w-4': '#0f766e',
  '--w-5': '#92400e', '--w-6': '#475569', '--w-7': '#0369a1', '--w-8': '#15803d',
  '--paper-2': '#F4F5F7',
  '--bg': '#E8E9EC',
  '--rule': 'rgba(22, 24, 27, 0.13)',
  '--rule-soft': 'rgba(22, 24, 27, 0.07)',
  '--rule-strong': 'rgba(22, 24, 27, 0.28)',
  '--hover': 'rgba(22, 24, 27, 0.05)',      // D-053: a row or list item under the pointer
  '--indigo': '#0443EF',
  '--indigo-ink': '#0337C2',
  // CD-25: the ONE mode-invariant ink — text/glyphs sitting ON a saturated
  // brand fill (a filled indigo button, a StatusTag, the paprika AI action).
  // White reads in day, sepia and night alike, which is why the design system
  // treats it as a deliberate constant; giving it a token name means component
  // CSS never has to reach for a raw literal to express it.
  '--on-brand': '#ffffff',
  // D-043 — see the dark map. Day paprika is too light for white (3.1:1).
  '--on-indigo': '#FFFFFF',
  '--on-paprika': '#16181B',         // = --carbon
  '--on-status-on-track': '#FFFFFF',
  '--paprika': '#F76100',
  '--paprika-ink': '#B54A08',
  // Scorecard-rebuild P1 (AUD-112): semantic status tokens — see the dark
  // map for the convention.
  '--status-on-track': '#2E7D32',
  '--status-on-track-soft': 'rgba(46, 125, 50, 0.14)',
  '--status-on-track-on-soft': '#1B5E20',
  '--status-off-track': '#D32F2F',
  '--status-off-track-soft': 'rgba(211, 47, 47, 0.12)',
  '--status-off-track-on-soft': '#B71C1C',
  '--status-warning': '#ED6C02',
  '--status-warning-soft': 'rgba(237, 108, 2, 0.14)',
  '--status-warning-on-soft': '#8F4700',
  '--status-neutral': '#5F6670',
  '--status-neutral-soft': 'rgba(95, 102, 112, 0.14)',
  '--status-neutral-on-soft': '#3F444B',
  // Stage-playbook STEP KINDS — see the dark set for the rationale.
  '--k-sched': '#0288D1',
  '--k-sched-soft': 'rgba(2, 136, 209, 0.11)',
  '--k-flow': '#7C5CFF',
  '--k-flow-soft': 'rgba(124, 92, 255, 0.11)',
  '--k-todo': '#5B7C99',
  '--k-todo-soft': 'rgba(91, 124, 153, 0.13)',
  '--k-form': '#D08C12',
  '--k-form-soft': 'rgba(224, 154, 31, 0.15)',
  '--k-new': '#2E7D32',
  '--k-new-soft': 'rgba(46, 125, 50, 0.12)',
  // Build blocks — see the dark map above for the family convention and why
  // this is not a status token. Values are the approved specimen's :root sweep.
  '--build': '#E09A1F',
  '--build-ink': '#8A5B08',
  '--build-soft': 'rgba(224, 154, 31, 0.13)',
  '--build-line': 'rgba(224, 154, 31, 0.48)',
  // Tech Audit BSG-only tint — see the dark map. The design's day values.
  '--internal-soft': '#FAF4E8',
  '--internal-rule': '#E4D3B3',
  '--internal-ink': '#7A5A1E',
};

// R3.2: sepia mode. DES-W1 (D-12, 2026-06-12): sepia Revised set per the
// approved contrast pitch (`sepiaRevised`). Parchment page vs ivory paper
// (value + slight hue separation kills "one yellow page"), warm hairlines
// that actually render on cream, warm ink deepened for reading-mode
// contrast.
const cssVarsSepia: Record<string, string> = {
  ...RAIL_TOKENS_SEPIA,
  ...V2_TOKENS,
  ...CHART_RAMP,
  '--rm-dot': V2_RM_DOT_DAY,
  '--header-bg': '#FCF7EB',
  '--header-bg-hover': '#F4EDDB',
  '--sticky-bg-alt': '#F6EFDD',
  '--accent-primary': '#0443ef',
  '--accent-primary-rgb': '4, 67, 239',
  '--accent-secondary': '#f76100',
  '--accent-secondary-rgb': '247, 97, 0',
  '--accent-info': '#0288d1',
  '--accent-error': '#d32f2f',
  '--donut-track': '#EBE2CD',
  '--text-muted': '#6C5C44',
  '--chart-1': '#0337C5',
  // The gap chart's owner ring and owner numbers (Phase 2d, InteractiveGapChart).
  '--slate': '#5B7C99', '--slate-soft': 'rgba(91,124,153,.16)', '--slate-on-soft': '#3D5A73',
  '--grid-line-rgb': '61, 46, 31',
  '--progress-track': '#EBE2CD',
  '--overlay-rgb': '61, 46, 31',
  '--header-text': '#2C2114',
  '--indigo-soft': 'rgba(4, 67, 239, 0.13)',
  '--paprika-soft': 'rgba(247, 97, 0, 0.15)',
  '--indigo-on-soft': '#022694',
  '--paprika-on-soft': '#8C3A00',
  '--paprika-nav-hover': 'rgba(247, 97, 0, 0.20)',
  // 5.5c brand tokens — sepia theme. Warm-dark text on ivory paper;
  // borders use the warm-dark hue at low opacity so they read on cream.
  '--ink': '#2C2114',
  '--ink-soft': '#4E402B',
  '--ink-faint': '#6C5C44',
  '--ink-disabled': '#98896F', // D-043 — see the dark map
  '--ink-ghost': 'var(--ink-disabled)',
  '--paper': '#FCF7EB',
  // DS2EOS IDS: the writer colours, one per person on a shared notebook, by
  // ((userId - 1) mod 8) + 1. Each reaches 4.5:1 on this mode's --paper, and
  // --on-indigo text reaches 4.5:1 on each.
  '--w-1': '#0443EF', '--w-2': '#B0155A', '--w-3': '#7A20C8', '--w-4': '#0E6E66',
  '--w-5': '#8A3C0C', '--w-6': '#4A5568', '--w-7': '#03628F', '--w-8': '#14703A',
  '--paper-2': '#F2EAD6',
  '--bg': '#E7DCC2',
  '--rule': 'rgba(56, 42, 26, 0.17)',
  '--rule-soft': 'rgba(56, 42, 26, 0.09)',
  '--rule-strong': 'rgba(56, 42, 26, 0.32)',
  '--hover': 'rgba(56, 42, 26, 0.06)',      // D-053: a row or list item under the pointer
  '--indigo': '#0337C5',
  '--indigo-ink': '#022694',
  // CD-25: the ONE mode-invariant ink — text/glyphs sitting ON a saturated
  // brand fill (a filled indigo button, a StatusTag, the paprika AI action).
  // White reads in day, sepia and night alike, which is why the design system
  // treats it as a deliberate constant; giving it a token name means component
  // CSS never has to reach for a raw literal to express it.
  '--on-brand': '#ffffff',
  // D-043 — see the dark map. Sepia's fills are deep, so all three stay white.
  '--on-indigo': '#FFFFFF',
  '--on-paprika': '#FFFFFF',
  '--on-status-on-track': '#FFFFFF',
  '--paprika': '#B84D00',
  '--paprika-ink': '#8C3A00',
  // Scorecard-rebuild P1 (AUD-112): semantic status tokens — sepia. Soft
  // tints bumped (like --indigo-soft) so they read on cream paper.
  '--status-on-track': '#2E7D32',
  '--status-on-track-soft': 'rgba(46, 125, 50, 0.16)',
  '--status-on-track-on-soft': '#1B5E20',
  '--status-off-track': '#C62828',
  '--status-off-track-soft': 'rgba(198, 40, 40, 0.14)',
  '--status-off-track-on-soft': '#8E1B1B',
  '--status-warning': '#D96400',
  '--status-warning-soft': 'rgba(217, 100, 0, 0.16)',
  '--status-warning-on-soft': '#80430A',
  '--status-neutral': '#6C5C44',
  '--status-neutral-soft': 'rgba(56, 42, 26, 0.13)',
  '--status-neutral-on-soft': '#4E402B',
  // Stage-playbook STEP KINDS — warmed a touch for the sepia paper.
  '--k-sched': '#0277A8',
  '--k-sched-soft': 'rgba(2, 119, 168, 0.12)',
  '--k-flow': '#6B4DE0',
  '--k-flow-soft': 'rgba(107, 77, 224, 0.12)',
  '--k-todo': '#55708A',
  '--k-todo-soft': 'rgba(85, 112, 138, 0.14)',
  '--k-form': '#B87A10',
  '--k-form-soft': 'rgba(184, 122, 16, 0.16)',
  '--k-new': '#2A6E2E',
  '--k-new-soft': 'rgba(42, 110, 46, 0.13)',
  // Build blocks — see the dark map for the family convention. The specimen
  // only sweeps day + night, so sepia follows this map's two standing rules:
  // deepen the base so it reads on cream, and bump the soft tint ~3 points.
  // Deliberately IDENTICAL to --k-form's amber here: sepia's amber slot is
  // already that value, and inventing a second near-identical sepia amber is
  // precisely how a palette drifts. The two never share a surface.
  '--build': '#B87A10',
  '--build-ink': '#8A5A08',
  '--build-soft': 'rgba(184, 122, 16, 0.16)',
  '--build-line': 'rgba(184, 122, 16, 0.42)',
  // Tech Audit BSG-only tint, sepia: the design's #FAF4E8 nearly IS sepia's
  // paper (#FCF7EB), so internal notes would read as client notes. A deeper
  // ochre that stands off the parchment; ink stays AA (5.8:1) on it.
  '--internal-soft': '#EFD5AE',
  '--internal-rule': '#C69C5B',
  '--internal-ink': '#6B4710',
};

/**
 * The full token map for a mode — for surfaces that pin their own mode
 * LOCALLY regardless of the app theme (the Process Roadmap canvas's
 * DAY/NIGHT toggle) by applying these as inline custom properties on
 * their subtree root. Read-only view of the same maps applyThemeCssVariables
 * writes to :root, so a locally-pinned surface can never drift from the
 * real theme values.
 */
export function themeCssVars(mode: ThemeMode): Record<string, string> {
  return mode === 'dark' ? cssVarsDark : mode === 'sepia' ? cssVarsSepia : cssVarsLight;
}

/**
 * Writes a mode's tokens onto `:root` and, by default, saves the mode as the
 * staff app's theme preference (`bsg-theme`).
 *
 * `persist: false` paints without saving. A public page (a booking link, a
 * form, a shared doc) always renders light, but a teammate who opens one in
 * the browser they use for the app must not find the app switched to light
 * next time it loads. Only the staff app's own theme switch saves the mode.
 */
export function applyThemeCssVariables(mode: ThemeMode, opts: { persist?: boolean } = {}): void {
  const root = document.documentElement;
  const vars = mode === 'dark' ? cssVarsDark : mode === 'sepia' ? cssVarsSepia : cssVarsLight;
  Object.entries(vars).forEach(([key, value]) => root.style.setProperty(key, value));
  // Transition tokens are theme-agnostic; apply alongside the per-mode set
  // so the InlineEdit primitives can rely on them being defined on :root.
  Object.entries(TRANSITION_TOKENS).forEach(([key, value]) => root.style.setProperty(key, value));
  // Carbon band tokens are mode-invariant by design (Wave M) — applied every
  // mode switch so they always exist on :root alongside the per-mode set.
  Object.entries(CARBON_TOKENS).forEach(([key, value]) => root.style.setProperty(key, value));
  Object.entries(BADGE_TOKENS).forEach(([key, value]) => root.style.setProperty(key, value));
  // Design System 2.0's shadows, corners, spacing and type sizes: the same in
  // every mode (the shadows that carry colour read the per-mode triplets).
  Object.entries(SYSTEM_TOKENS).forEach(([key, value]) => root.style.setProperty(key, value));
  root.setAttribute('data-theme', mode);
  if (opts.persist !== false) localStorage.setItem('bsg-theme', mode);
}
